<template>
  <ClientOnly>
    <div
      ref="dialogRef"
      class="dialog-container hide-scrollbar overflow-y-auto max-h-[97%] top-2/4 left-2/4 fixed bg-white z-50 transform -translate-x-1/2 -translate-y-1/2 rounded"
      :class="customClass"
      :style="{ 'max-width': maxWidth, 'min-width': minWidth }"
      :data-intercom-target="dataTarget"
    >
      <div class="dialog-content" @click.stop>
        <div
          class="dialog-title font-medium text-base border-b border-accent-200 h-[54px]"
        >
          <div
            class="flex flex-row py-4 pr-4 pl-5"
            :class="`justify-${titleAlign === 'left' ? 'between' : 'center'} ${titleClass}`"
          >
            <div class="flex flex-row items-center">
              <slot name="titleIcon">
                <span
                  v-if="titleSvgIcon && titleSvgIcon.length"
                  class="flex items-center justify-center"
                >
                  <img
                    :src="titleSvgIcon"
                    alt=""
                    aria-hidden="true"
                    :class="`${titleIconClass} text-${buttonColor}`"
                    :style="titleIconCustomStyle"
                  />
                </span>
                <span
                  v-else-if="titleIcon && titleIcon.length"
                  class="flex items-center justify-center"
                >
                  <font-awesome-icon
                    :class="`${titleIconClass} text-${buttonColor}`"
                    :icon="titleIcon"
                    :style="titleIconCustomStyle"
                /></span>
              </slot>
              <div
                class="text-accent-900 font-medium font-sans text-base not-italic"
                :class="titleTextClass"
              >
                {{ dialogTitle }}
              </div>
            </div>
            <div
              v-if="hasExitButton"
              class="inline-flex items-center justify-center min-w-[24px] min-h-[24px] p-[2px] rounded-[6px] gap-[2px]"
            >
              <button
                class="p-0.5 flex items-center justify-center rounded-md text-accent-500 h-5 w-5"
                @click="emit('cancel')"
              >
                <font-awesome-icon
                  :icon="['fas', 'xmark-large']"
                  class="close-btn text-[10px] p-[2px] leading-none w-4 h-4"
                />
              </button>
            </div>
          </div>
        </div>

        <div
          class="p-5 text-sm font-medium leading-6 text-accent-500"
          :style="bodyCustomStyle"
        >
          <slot />
        </div>

        <div
          class="flex flex-row justify-between pl-5 py-4 pr-4 h-[68px]"
          :class="footerCustomClass"
          :style="footerCustomStyle"
        >
          <div v-if="displayCheckbox" class="flex flex-row items-center">
            <OnclusiveCheckbox
              class="mr-3"
              :checked="dialogCheckboxStatus"
              @checked="checkboxClicked"
            />
            {{ checkboxLabel }}
          </div>
          <div v-else class="flex flex-1" />
          <div v-if="hasFooter" class="flex flex-row justify-end gap-3">
            <PrimaryButton
              v-if="hasCancelButton"
              :variant="cancelButtonVariant"
              :data-target="dataTargetCancelButton"
              @on-click="cancel()"
            >
              {{ cancelButtonText }}
            </PrimaryButton>
            <PrimaryButton
              v-if="hasConfirmButton"
              :loading="isLoading"
              :custom-class="!isButtonDisabled ? `bg-${buttonColor}` : ''"
              :variant="dialogType"
              :disabled="isButtonDisabled"
              :data-target="dataTargetSaveButton"
              @on-click="confirm()"
            >
              <span class="flex items-center gap-2">
                <font-awesome-icon
                  v-if="hasConfirmButtonIcon"
                  :icon="['far', icon]"
                />
                <span>{{ confirmButtonText }}</span>
              </span>
            </PrimaryButton>
          </div>
          <slot name="footer">
            <!-- Custom footer content can be inserted here -->
          </slot>
        </div>
      </div>
    </div>
    <div
      class="background-tint fixed top-0 left-0 w-full h-full z-40 bg-opacity-50 bg-black"
      @click="cancel"
      @click.stop
    />
  </ClientOnly>
</template>

<script setup lang="ts">
import { Ref, ref, CSSProperties } from "vue";
import { onClickOutside } from "@vueuse/core";
import PrimaryButton from "~/components/shared/primary-button/PrimaryButton.vue";
import OnclusiveCheckbox from "~/components/shared/onclusive-checkbox/OnclusiveCheckbox.vue";
import { useCheckIntercomTourExists } from "@/composables/useCheckIntercomTourExists";

const dialogCheckboxStatus: Ref<boolean> = ref(false);
const dialogRef = ref(null);

const emit = defineEmits(["cancel", "confirm", "checkboxClicked"]);

const { checkIntercomTourExists } = useCheckIntercomTourExists();

onClickOutside(dialogRef, () => handleCloseDialog(), {
  ignore: [".el-popper", ".el-overlay"],
});

const props = defineProps({
  dialogTitle: {
    type: String,
    default: "Default Title",
    required: true,
  },

  maxWidth: {
    type: String,
    default: "100%",
    required: false,
  },

  minWidth: {
    type: String,
    default: "",
    required: false,
  },

  cancelButtonText: {
    type: String,
    default: "Cancel",
    required: false,
  },

  confirmButtonText: {
    type: String,
    default: "Save",
    required: false,
  },

  hasExitButton: {
    type: Boolean,
    default: false,
  },

  buttonColor: {
    type: String,
    default: "secondary-700",
  },

  dialogType: {
    type: String,
    default: "primary",
  },

  hasConfirmButton: {
    type: Boolean,
    default: true,
  },

  hasConfirmButtonIcon: {
    type: Boolean,
    default: false,
  },

  icon: {
    type: String,
    default: "",
    required: false,
  },

  isButtonDisabled: {
    type: Boolean,
    default: true,
  },

  hasCancelButton: {
    type: Boolean,
    default: true,
  },

  displayCheckbox: {
    type: Boolean,
    default: false,
  },

  checkboxLabel: {
    type: String,
    default: "",
    required: false,
  },

  isLoading: {
    type: Boolean,
    default: false,
    required: false,
  },

  dataTarget: {
    type: String,
    default: "",
  },

  dataTargetSaveButton: {
    type: String,
    default: "Save",
  },

  dataTargetCancelButton: {
    type: String,
    default: "Cancel",
  },

  titleSvgIcon: {
    type: String,
    default: "",
  },

  titleIcon: {
    type: Array,
    default: () => [],
  },

  titleIconClass: {
    type: String,
    default: "text-lg mr-3",
  },

  titleAlign: {
    type: String,
    default: "left",
  },

  titleClass: {
    type: String,
    default: "py-4 pr-4 pl-5",
  },

  hasFooter: {
    type: Boolean,
    default: true,
  },

  bodyCustomStyle: {
    type: Object as () => CSSProperties,
    default: () => ({}),
  },

  footerCustomStyle: {
    type: Object as () => CSSProperties,
    default: () => ({}),
  },

  titleIconCustomStyle: {
    type: Object as () => CSSProperties,
    default: () => ({}),
  },
  footerCustomClass: {
    type: String,
    default: "",
  },
  cancelButtonVariant: {
    type: String,
    default: "secondary",
  },

  customClass: {
    type: String,
    default: "",
  },

  titleTextClass: {
    type: String,
    default: "text-accent-900",
  },
});

const handleCloseDialog = () => {
  if (!checkIntercomTourExists()) {
    emit("cancel");
  }
};

const cancel = () => {
  emit("cancel");
};

const confirm = () => {
  if (!props.isButtonDisabled) {
    emit("confirm");
  }
};

const checkboxClicked = (value: boolean) => {
  dialogCheckboxStatus.value = value;
  emit("checkboxClicked", value);
};
</script>
